import React, { Fragment, useState, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { updateDb } from "../services/updateDb";

import AlertMessage from "../common/AlertMessage";
import LoadPage from "../common/LoadPage";
import useAxiosFetch from "../common/useAxiosFetch";
import ConfirmationModal from "../common/ConfirmationModal";
import { useAppContext } from "../AppContext";
import moment from "moment";
import { useTranslation } from 'react-i18next';

const EnvironmentPage = () => {
  const { alertMessage, showAlertMessage, currentUser } = useAppContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loading, refetch } = useAxiosFetch("/api/environments"); //forces a refresh when risk is updated/deleted

  let navigateEnvironmentDetails = (envId) => {
    navigate(`/viewenvironment?envId=${envId}`);
  };

  const [show, setShow] = useState(false);
  const [deleteEnvironment, setDeleteEnvironment] = useState();

  const handleClose = async (deleteEnvironment, envId) => {
    setShow(false);

    if (deleteEnvironment && envId) {
      const res = await updateDb("/api/environments/" + envId, {}, "delete");

      if (res.success) {
        refetch();
        showAlertMessage('error', t('environment_deleted'));
      }
      else {
        showAlertMessage('error', res.error ? res.error : t('error_deleting_environment'));
      }
    }
  };

  const handleShow = (envId) => {
    setDeleteEnvironment(envId);
    setShow(true);
  };

  // Helper to safely get count value
  const getCount = (value) => {
    if (typeof value === 'number') return value;
    if (value === null || value === undefined) return 0;
    return 0;
  };

  if (loading)
    return <LoadPage />;

  if (!data || !Array.isArray(data))
    return <LoadPage />;

  return (
    <div id="content">

      <ConfirmationModal
        show={show}
        handleClose={handleClose}
        deleteId={deleteEnvironment}
      />

      {currentUser.environmentId === 'main' && (
        <div className="content-header">
          <NavLink key="addEnvironment" to="/addenvironment">
            <div className="white-border-btn">+ {t('add_environment')}</div>
          </NavLink>
        </div>
      )}
      
      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble content-bubble-table">

        <table className="overview-table">
          <thead>
            <tr>
              <td>{t('environment')}</td>
              <td>{t('status')}</td>
              <td>{t('plan')}</td>
              <td>{t('orgs')}</td>
              <td>{t('domains')}</td>
              <td>{t('users')}</td>
              <td>{t('ai_per_week')}</td>
              <td>{t('trial_exp')}</td>
              <td>{t('subscription_exp')}</td>
              {currentUser.environmentId === 'main' && <td>{t('actions')}</td>}
            </tr>
          </thead>
          <tbody>
            {data.map((item) => {
              const now = new Date().getTime();
              const isMainEnvironment = item.name === "main" || item.name === "knmt";
              const isTrialEnvironment = item.trialExpires && item.trialExpires > 0;
              const hasTrialPlan = item.subscriptionPlan?.planId === 'trial';
              const hasActiveSubscription = item.subscriptionExpires && item.subscriptionExpires > now;
              const hasActiveTrial = item.trialExpires && item.trialExpires > now;
              const isManualEnvironment = !isMainEnvironment && (!item.trialExpires || item.trialExpires === 0);
              
              // Can delete: environments with trial plan or trialExpires set, that have expired and have no active subscription
              // Cannot delete: manually created environments, active trials, or environments with active subscription
              const canDelete = (isTrialEnvironment || hasTrialPlan) && !hasActiveTrial && !hasActiveSubscription;
              
              // Display values
              const trialExpiresDisplay = item.trialExpires && item.trialExpires > 0 
                ? (hasActiveTrial ? moment(item.trialExpires).format('MMM D, YYYY') : moment(item.trialExpires).fromNow()) 
                : "-";
              const subscriptionExpiresDisplay = item.subscriptionExpires 
                ? (hasActiveSubscription ? moment(item.subscriptionExpires).format('MMM D, YYYY') : moment(item.subscriptionExpires).fromNow()) 
                : "-";
              
              // Get plan info - handle environments without subscriptionPlan assigned
              const planId = item.subscriptionPlan?.planId;
              
              // Display plan name with fallback for missing data
              let planName;
              if (isMainEnvironment) {
                planName = t('super');
              } else if (planId) {
                planName = planId.charAt(0).toUpperCase() + planId.slice(1);
              } else {
                // No plan assigned - show warning
                planName = <span style={{ color: '#FF9800' }}>{t('not_set')}</span>;
              }
              
              // All plans are yearly now, no need for billing period display
              
              // Get feature limits (convert -1 to ∞)
              const getLimit = (overrideValue, defaultValue) => {
                if (overrideValue !== undefined && overrideValue !== null) {
                  return overrideValue === -1 ? '∞' : overrideValue;
                }
                return defaultValue;
              };
              
              // Default limits based on plan (with fallback for missing plans)
              const getDefaultLimit = (featureName) => {
                if (isMainEnvironment) return '∞';
                if (!planId) {
                  // No plan assigned - default to limited: 1 org, 1 project, 1 user, 5 AI calls
                  return featureName === 'orgs' ? 1 : featureName === 'projects' ? 1 : featureName === 'users' ? 1 : featureName === 'ai' ? 5 : '∞';
                }
                
                if (planId === 'trial') {
                  return featureName === 'orgs' ? 1 : featureName === 'projects' ? 1 : featureName === 'users' ? 1 : featureName === 'ai' ? 5 : '∞';
                } else if (planId === 'free') {
                  return featureName === 'ai' ? 50 : '∞';
                } else if (planId === 'starter') {
                  return featureName === 'orgs' ? 1 : featureName === 'projects' ? 1 : featureName === 'ai' ? 5 : '∞';
                } else if (planId === 'premium') {
                  return featureName === 'ai' ? 50 : '∞';
                }
                return '∞';
              };
              
              const maxOrgs = getLimit(
                item.subscriptionPlan?.featureOverrides?.maxOrganizations,
                getDefaultLimit('orgs')
              );
              const maxProjects = getLimit(
                item.subscriptionPlan?.featureOverrides?.maxProjects,
                getDefaultLimit('projects')
              );
              const maxUsers = getLimit(
                item.subscriptionPlan?.featureOverrides?.maxUsers,
                getDefaultLimit('users')
              );
              const aiCallsPerWeek = getLimit(
                item.subscriptionPlan?.featureOverrides?.aiCallsPerWeek,
                getDefaultLimit('ai')
              );
              
              // Determine status and styling based on plan and subscription state
              let statusText;
              let statusStyle;
              
              if (isMainEnvironment) {
                statusText = t('super');
                statusStyle = { color: "#9C27B0", fontWeight: "bold" };
              } else if (planId === 'free') {
                // Free plan - always active
                statusText = t('free');
                statusStyle = { color: "#2196F3", fontWeight: "bold" };
              } else if (hasActiveSubscription) {
                // Has paid subscription that's not expired
                statusText = t('active');
                statusStyle = { color: "#4CAF50", fontWeight: "bold" };
              } else if (hasActiveTrial) {
                // Trial period still active
                statusText = t('trial');
                statusStyle = { color: "#FF9800", fontWeight: "bold" };
              } else if (planId === 'trial' || isTrialEnvironment) {
                // Trial plan but expired
                statusText = t('expired');
                statusStyle = { color: "#f44336", fontWeight: "bold" };
              } else if (!planId) {
                // No plan set
                statusText = t('not_set');
                statusStyle = { color: "#FF9800", fontWeight: "bold" };
              } else {
                // Has a plan but subscription expired
                statusText = t('expired');
                statusStyle = { color: "#f44336", fontWeight: "bold" };
              }
              
              return (
                <Fragment key={item["id"]}>
                  <tr>
                    <td 
                      className={currentUser.environmentId === 'main' ? "pointer-class" : ""} 
                      onClick={currentUser.environmentId === 'main' ? () => navigateEnvironmentDetails(item.id) : undefined}
                    >
                      <strong>{item.name === "www" ? "main" : item.name}</strong>
                    </td>
                    <td style={statusStyle}>{statusText}</td>
                    <td>
                      <span style={{ fontWeight: '500' }}>{planName}</span>
                      {item.subscriptionPlan?.featureOverrides && Object.keys(item.subscriptionPlan.featureOverrides).length > 0 && (
                        <span style={{ fontSize: '0.8em', color: '#FF9800', marginLeft: '5px' }} title="Has custom feature overrides">
                          ⚙️
                        </span>
                      )}
                    </td>
                    <td>
                      {getCount(item.organizations)}/{typeof maxOrgs === 'object' ? maxOrgs : (maxOrgs === '∞' ? '∞' : maxOrgs)}
                      {typeof maxOrgs === 'number' && getCount(item.organizations) > maxOrgs && (
                        <span style={{ color: '#f44336', marginLeft: '3px' }}>⚠️</span>
                      )}
                    </td>
                    <td>
                      {getCount(item.projects)}/{typeof maxProjects === 'object' ? maxProjects : (maxProjects === '∞' ? '∞' : maxProjects)}
                      {typeof maxProjects === 'number' && getCount(item.projects) > maxProjects && (
                        <span style={{ color: '#f44336', marginLeft: '3px' }}>⚠️</span>
                      )}
                    </td>
                    <td>
                      {getCount(item.users)}/{typeof maxUsers === 'object' ? maxUsers : (maxUsers === '∞' ? '∞' : maxUsers)}
                      {typeof maxUsers === 'number' && getCount(item.users) > maxUsers && (
                        <span style={{ color: '#f44336', marginLeft: '3px' }}>⚠️</span>
                      )}
                    </td>
                    <td>{typeof aiCallsPerWeek === 'object' ? aiCallsPerWeek : (aiCallsPerWeek === '∞' ? '∞' : aiCallsPerWeek)}</td>
                    <td style={{ fontSize: '0.85em' }}>{trialExpiresDisplay}</td>
                    <td style={{ fontSize: '0.85em' }}>{subscriptionExpiresDisplay}</td>

                    {currentUser.environmentId === 'main' && (
                      <td>
                        {(item.name !== "www") && (
                          <div>
                            <i 
                              className="icon-edit" 
                              style={{ cursor: 'pointer', color: 'var(--text-color)' }}
                              title={t('manage_subscription_features')}
                              onClick={() => navigate(`/manageenvironment?envId=${item.id}`)}
                            />
                            {canDelete ? (
                              <i 
                                onClick={() => handleShow(item.id)} 
                                className="icon-delete" 
                                style={{ cursor: 'pointer', color: 'var(--red-risk-score)' }}
                                title={t('delete_environment')}
                              />
                            ) : (
                              <i 
                                className="icon-delete" 
                                style={{ color: "lightgray", cursor: "not-allowed" }} 
                                title={hasActiveSubscription ? t('cannot_delete_active_subscription') : t('cannot_delete_manual_environment')} 
                              />
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EnvironmentPage;